import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk';

import { fetchIntercomJwt } from '../util/api';
import { useIframeDetection } from './useIframeDetection';

const APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

// The SDK's initialiser injects the script only once; every later change has to
// go through boot/update. This flag tracks whether that first call happened.
let isScriptLoaded = false;

const toUnixSeconds = date => {
  const time = date instanceof Date ? date.getTime() : Date.parse(date);
  return Number.isFinite(time) ? Math.floor(time / 1000) : undefined;
};

/**
 * Builds the identity payload for an authenticated user.
 *
 * Guests intentionally get no identity attributes at all: Intercom treats them
 * as visitors and asks for an email itself once they start a conversation, so
 * there is nothing for us to collect or fake here.
 */
const getIdentity = currentUser => {
  const userId = currentUser?.id?.uuid;
  if (!userId) {
    return null;
  }

  const { email, createdAt, profile } = currentUser.attributes || {};
  const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');
  const createdAtUnix = toUnixSeconds(createdAt);

  return {
    user_id: userId,
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
    ...(createdAtUnix ? { created_at: createdAtUnix } : {}),
  };
};

/**
 * Boots and maintains the Intercom Messenger for the whole marketplace.
 *
 * Scope decision: the Messenger is loaded on bowtackle.com only. Inside the
 * dealer iframe it is never initialised, so the script never reaches dealer
 * pages and the panel can never render clipped inside the embed.
 * See docs/intercom-messenger-current-behavior.md
 */
export const useIntercom = () => {
  const isIframe = useIframeDetection();
  const location = useLocation();
  const currentUser = useSelector(state => state.user.currentUser);

  const identity = getIdentity(currentUser);
  const userId = identity?.user_id || null;

  // Keeps the previous user id so we can tell login from logout.
  const previousUserIdRef = useRef(null);
  // Whether this session has an Intercom instance running right now.
  const hasBootedRef = useRef(false);

  const isEnabled = !!APP_ID && !isIframe;

  useEffect(() => {
    if (!isEnabled) {
      // Becoming disabled after the Messenger booted has to tear it down, not
      // merely stop updating it, so that "never loaded inside the dealer iframe"
      // holds for any future change in what `useIframeDetection` reports.
      if (hasBootedRef.current) {
        shutdown();
        hasBootedRef.current = false;
        previousUserIdRef.current = null;
      }
      return;
    }

    let isStale = false;

    const bootMessenger = async () => {
      const previousUserId = previousUserIdRef.current;
      previousUserIdRef.current = userId;

      // Logging out must clear Intercom's cookies, otherwise the next person on
      // this device inherits the previous user's support session.
      //
      // Logging in deliberately does NOT shut down first: Intercom merges a
      // lead into the user through that same device cookie, and shutting down
      // would throw away the conversation the guest just had.
      const isLogout = !!previousUserId && !userId;
      if (isLogout) {
        shutdown();
      }

      // Messenger security is enforced for identified users only; an anonymous
      // boot never carries a token.
      const jwt = userId ? await fetchIntercomJwt() : null;
      if (isStale) {
        return;
      }

      const settings = {
        app_id: APP_ID,
        ...(identity || {}),
        ...(jwt ? { intercom_user_jwt: jwt } : {}),
      };

      if (!isScriptLoaded) {
        Intercom(settings);
        isScriptLoaded = true;
      } else {
        boot(settings);
      }
      hasBootedRef.current = true;
    };

    bootMessenger();

    return () => {
      isStale = true;
    };
    // Note: `identity` is intentionally not a dependency. It is derived from
    // `userId`, so keying on the id gives one boot per session change instead of
    // one per currentUser object identity change.
  }, [isEnabled, userId]);

  // Intercom checks for new messages and auto-messages on update, so it needs
  // to know about client-side route changes in this SPA.
  useEffect(() => {
    // `hasBootedRef` rather than `isScriptLoaded`: the script survives a
    // shutdown, so the module flag alone would keep pinging a torn-down instance.
    if (!isEnabled || !hasBootedRef.current) {
      return;
    }
    update({ last_request_at: Math.floor(Date.now() / 1000) });
  }, [isEnabled, location?.pathname]);
};

export default useIntercom;
